import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BookOpenCheck,
  Quote,
  Sparkles,
  GraduationCap,
  FileText,
  FileSearch,
  Mic2,
  Repeat,
  Save,
  Mic,
  Upload,
  SlidersHorizontal,
  Pencil,
  Languages,
  ClipboardList,
  BadgeCheck,
  Download,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
export const Route = createFileRoute("/features")({
  head: () => ({
    meta: [
      { title: "Features — SmartEssay" },
      {
        name: "description",
        content:
          "Every SmartEssay feature in one place: voice matching, anti-repetition guard, real MLA & APA 7 quotes & PDFs, peer-review summaries, Essay Reviewer, dictation, drag-and-drop sources, and auto-saved history.",
      },
      { property: "og:title", content: "Features — SmartEssay" },
      {
        property: "og:description",
        content:
          "Voice matching, anti-repetition guard, real citations, MLA & APA 7 PDFs, Essay Reviewer, and more.",
      },
      { property: "og:url", content: "https://smartessay.ai/features" },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/features" }],
  }),
  component: FeaturesPage,
});

const FEATURES = [
  {
    icon: BookOpenCheck,
    title: "Three modes",
    body: "Essay, Summary, or Key Concepts — as a full sample or a guided outline. Intermediate, advanced, or graduate voice.",
  },
  {
    icon: Quote,
    title: "Real quotes",
    body: "Word-for-word passages with proper MLA or APA 7 citations every time. Customize evidence per claim from 1 up to 10.",
  },
  {
    icon: Sparkles,
    title: "Peer-reviewed summaries",
    body: "Paste a research article and get a structured APA 7 summary: Introduction, Participants, Measurement, Results, Critique.",
  },
  {
    icon: Mic2,
    title: "Voice matching",
    body: "Paste a writing sample — or upload a voice or video recording — and the draft mirrors your sentence length, vocabulary, and rhythm. Save a permanent profile or override per essay.",
  },
  {
    icon: Repeat,
    title: "Anti-repetition guard",
    body: "Every generation bans the most over-used AI words (delve, tapestry, moreover…) and enforces sentence variety so prose reads fresh.",
  },
  {
    icon: FileText,
    title: "MLA & APA 7 PDFs",
    body: "Export a polished draft with proper heading, student title page, and Works Cited or References. APA exports automatically italicize titles and journal names.",
  },
  {
    icon: SlidersHorizontal,
    title: "Tone & level switcher",
    body: "Flip any outline between Academic and Natural voice, or Intermediate and Advanced vocabulary, with one click.",
  },
  {
    icon: Upload,
    title: "Drag-and-drop sources",
    body: "Drop a .txt, .md, .pdf, or .docx straight into the source area — we extract the text for you.",
  },
  {
    icon: Mic,
    title: "Voice dictation",
    body: "Tap the mic next to any field to dictate your title, prompt, or source instead of typing.",
  },
  {
    icon: Save,
    title: "Auto-saved history",
    body: "Every outline you generate is saved to Past outlines — searchable, filterable, deletable in bulk.",
  },
  {
    icon: FileSearch,
    title: "Essay Reviewer",
    body: "Already wrote a draft? Get evidence gaps, a paragraph-by-paragraph flow map, and prioritized fixes.",
  },
  {
    icon: Pencil,
    title: "Build Your Own (Tutor)",
    body: "Draft section-by-section with sentence frames, live word counts, and source-aware suggestions. You stay in the driver's seat — the tutor just nudges.",
  },
  {
    icon: Languages,
    title: "Decode the prompt",
    body: "One click rewrites a confusing assignment in plain English so you actually know what you're being asked to write.",
  },
  {
    icon: ClipboardList,
    title: "Tailored requirements",
    body: "Set the number of body paragraphs, target word count, and required evidence per claim — the outline matches exactly.",
  },
  {
    icon: BadgeCheck,
    title: "Title verification",
    body: "We confirm the book, article, or paper you cite before pulling quotes, so evidence never comes from the wrong work.",
  },
  {
    icon: Copy,
    title: "Copy to Google Docs",
    body: "One-click copy formats your outline cleanly for Google Docs, Word, or any editor — headings, citations, and all.",
  },
  {
    icon: Download,
    title: "Export your data",
    body: "Download a complete JSON of your profile, outlines, and history any time from your account.",
  },
  {
    icon: GraduationCap,
    title: "Free to try",
    body: "First outline is on us — no credit card required. Upgrade only when you're sure.",
  },
];

function FeaturesPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-6 pb-24">
        <section className="pt-12 text-center sm:pt-16">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">
            What's included
          </p>
          <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
          <h1 className="mt-4 font-display text-4xl font-medium sm:text-5xl">
            Every SmartEssay feature
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Real quotes & citations, voice that sounds like you, polished PDFs,
            and tools that keep your thinking front and center.
          </p>
        </section>

        <section className="mt-12">
          <div className="grid gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-card p-7 text-left transition-colors hover:bg-secondary/40"
              >
                <Icon className="mb-4 h-5 w-5 text-primary" />
                <h2 className="font-display text-lg font-medium">{title}</h2>
                <div className="my-3 h-px w-8 bg-gold/60" aria-hidden />
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 flex flex-wrap items-center justify-center gap-3">
          <Link to="/auth">
            <Button size="lg" className="shadow-glow">
              Start free
            </Button>
          </Link>
          <Link to="/pricing">
            <Button size="lg" variant="outline">
              See pricing
            </Button>
          </Link>
          <Link to="/help">
            <Button size="lg" variant="ghost">
              How it works
            </Button>
          </Link>
        </section>
      </main>

    </div>
  );
}
